import React from 'react';
import { SectionShell } from '../primitives/SectionShell';
import { SectionHeader } from '../primitives/SectionHeader';
import { Editable } from '../inline/Editable';
import type { SectionBaseConfig } from '../../../storefront-schema/types';

export type PaymentBrand =
  | 'visa'
  | 'mastercard'
  | 'amex'
  | 'paypal'
  | 'apple-pay'
  | 'google-pay'
  | 'fpx'
  | 'stripe'
  | 'shopback'
  | 'grabpay'
  | 'tng'
  | 'boost'
  | 'custom';

export interface PaymentIcon {
  id: string;
  brand: PaymentBrand;
  label?: string;
  logoUrl?: string;
}

export interface PaymentIconsConfig extends SectionBaseConfig {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  icons?: PaymentIcon[];
  layout?: 'row' | 'grid';
  align?: 'left' | 'center' | 'right';
  showLockNote?: boolean;
  lockNote?: string;
}

const brandMeta: Record<PaymentBrand, { label: string }> = {
  visa: { label: 'Visa' },
  mastercard: { label: 'Mastercard' },
  amex: { label: 'American Express' },
  paypal: { label: 'PayPal' },
  'apple-pay': { label: 'Apple Pay' },
  'google-pay': { label: 'Google Pay' },
  fpx: { label: 'FPX' },
  stripe: { label: 'Stripe' },
  shopback: { label: 'ShopBack' },
  grabpay: { label: 'GrabPay' },
  tng: { label: 'Touch ‘n Go eWallet' },
  boost: { label: 'Boost' },
  custom: { label: 'Pay' },
};

// Official brand card badges — the real, universally-recognised colour logos
// (Visa blue card, Mastercard slate, Amex blue, PayPal, Apple Pay, Google Pay).
// Bundled as static SVG assets under /public/payment-icons so they render
// pixel-accurate at any size, identically across every device. Sourced from the
// open-source aaronfagan/svg-credit-card-payment-icons + datatrans/payment-logos
// brand-logo sets. BASE_URL keeps them resolving under any storefront host.
const ASSET_BASE = `${import.meta.env.BASE_URL || '/'}payment-icons/`;
const OFFICIAL: Partial<Record<PaymentBrand, string>> = {
  visa: `${ASSET_BASE}visa.svg`,
  mastercard: `${ASSET_BASE}mastercard.svg`,
  amex: `${ASSET_BASE}amex.svg`,
  paypal: `${ASSET_BASE}paypal.svg`,
  'apple-pay': `${ASSET_BASE}apple-pay.svg`,
  'google-pay': `${ASSET_BASE}google-pay.svg`,
  fpx: `${ASSET_BASE}fpx.svg`,
};

// Local rails without a ubiquitous vector (Malaysian FPX + optional wallets):
// drawn as matching full-bleed cards so they sit consistently beside the
// official brand badges. viewBox 0 0 68 44 == the badge aspect, so the card
// fills edge-to-edge; rounded corners come from the container's overflow-hidden.
type CardStyle = { bg: string; ink: string; text: string; size?: number; ls?: number; italic?: boolean };
const CARD: Partial<Record<PaymentBrand, CardStyle>> = {
  stripe: { bg: '#635BFF', ink: '#ffffff', text: 'stripe', size: 15, ls: -0.4, italic: true },
  tng: { bg: '#0066FF', ink: '#ffffff', text: 'TNG', size: 15, ls: 0.6 },
  grabpay: { bg: '#ffffff', ink: '#00B14F', text: 'GrabPay', size: 12 },
  shopback: { bg: '#ffffff', ink: '#FF5A5F', text: 'ShopBack', size: 11 },
  boost: { bg: '#ffffff', ink: '#EE2737', text: 'Boost', size: 14 },
  custom: { bg: '#1f2937', ink: '#ffffff', text: 'Pay', size: 13 },
};

const BrandLogo: React.FC<{ brand: PaymentBrand }> = ({ brand }) => {
  const c = CARD[brand] || CARD.custom!;
  return (
    <svg viewBox="0 0 68 44" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <rect width="68" height="44" fill={c.bg} />
      <text
        x="34"
        y="27"
        textAnchor="middle"
        fontFamily="Helvetica Neue, Arial, sans-serif"
        fontWeight={800}
        fontStyle={c.italic ? 'italic' : 'normal'}
        fontSize={c.size || 13}
        letterSpacing={c.ls || 0}
        fill={c.ink}
      >
        {c.text}
      </text>
    </svg>
  );
};

export const PaymentIconsSection: React.FC<{ config?: PaymentIconsConfig }> = ({ config }) => {
  const icons = config?.icons?.length
    ? config.icons
    : [
        { id: '1', brand: 'visa' as const },
        { id: '2', brand: 'mastercard' as const },
        { id: '3', brand: 'amex' as const },
        { id: '4', brand: 'paypal' as const },
        { id: '5', brand: 'apple-pay' as const },
        { id: '6', brand: 'google-pay' as const },
        { id: '7', brand: 'fpx' as const },
      ];
  const align = config?.align || 'center';
  const layout = config?.layout || 'row';
  const showLockNote = config?.showLockNote !== false;
  const lockNote =
    config?.lockNote || 'Secure SSL checkout — your payment details are encrypted.';

  const justify =
    align === 'left'
      ? 'justify-start'
      : align === 'right'
        ? 'justify-end'
        : 'justify-center';

  const wrapperClass =
    layout === 'grid'
      ? 'grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-3'
      : `flex flex-wrap items-center gap-3 ${justify}`;

  return (
    <SectionShell config={config} className="!py-8">
      {(config?.eyebrow || config?.title || config?.subtitle) && (
        <SectionHeader
          eyebrow={config?.eyebrow}
          title={config?.title}
          subtitle={config?.subtitle}
        />
      )}
      <div className={wrapperClass}>
        {icons.map((p) => {
          const meta = brandMeta[p.brand] || brandMeta.custom;
          const label = p.label || meta.label;
          const assetSrc = p.logoUrl || OFFICIAL[p.brand];
          return (
            <div
              key={p.id}
              className="flex items-center justify-center h-11 w-[68px] rounded-[4px] overflow-hidden select-none transition-shadow"
              style={{
                boxShadow: '0 0 0 1px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
              }}
              aria-label={label}
              title={label}
            >
              {assetSrc ? (
                <img
                  loading="lazy"
                  decoding="async"
                  src={assetSrc}
                  alt={label}
                  className="w-full h-full object-cover"
                />
              ) : (
                <BrandLogo brand={p.brand} />
              )}
            </div>
          );
        })}
      </div>
      {showLockNote && (
        <p
          className={`mt-4 text-xs opacity-70 ${align === 'left' ? 'text-left' : align === 'right' ? 'text-right' : 'text-center'}`}
        >
          <Editable as="span" field="config.lockNote" value={lockNote} />
        </p>
      )}
    </SectionShell>
  );
};

export default PaymentIconsSection;
